/**
 * @file auditDetails.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-21
 * @modified 2026-11-03
 * @description What an audit entry's `changes` say, as rows a person can read —
 *              shared by the admin Activity screen and the history window
 *              (AuditHistoryModal, PlanHistoryModal).
 *
 *              Cam, 21 Oct 2026: "The admin activity log is not showing details
 *              that it should regarding costing and pricing activity." Both
 *              renderers read only `{ old, new }` pairs, so everything Pricing &
 *              Costing, the rep-reply import, the Xero cost push and the bill
 *              write-back record in any other shape was shown as an empty row
 *              ("Lines", "File" with nothing beside them) or a JSON dump:
 *
 *                PRICING_CONFIRMED     { lines: [{ side, value, source, source_ref, product, order|line_id }] }
 *                REP_PRICE…            { lines: [{ product, line_id, rep_price, notes?, kept? }], file }
 *                CONTRACT_FROM_REP_REPLY { price, order_lines: [ids], writes: ['create #2748'], file }
 *                PRICE_REQUESTED       { price_requested: { new: { to, line_ids, spreadsheet, tracking_id } } }
 *                XERO_COST_PUSHED      { xero_cost: { new: { sent, unchanged, unverified, xero_status } } }
 *                XERO_PO_SYNCED / _FAILED { xero_sync: { new: { sent, verified, xero_status } | { error, code, … } },
 *                                        lines_changed, lines_added, lines_removed, header }  (1.1.0, 2026-10-22)
 *                INVOICE_CUSTOMER_SET  { invoice_to, lines: 1 }
 *                LINE_UPDATE (a cost)  { unit_price: {old,new}, _product, … }  — "Unit Price" on a PO is its COST
 *
 *              describeChanges() turns every visible field into one of three
 *              row kinds; the screens render them with their existing markup.
 *                { kind: 'pair',  field, label, old, new, hasOld, hasNew }   old → new
 *                { kind: 'value', field, label, value, text? }               one value
 *                { kind: 'list',  field, label, items: [string] }            lines, each one sentence
 *              Underscore fields stay hidden (route metadata), except the few a
 *              reader needs (SHOWN_META). Nothing here fetches or writes.
 */

import { formatPrice } from './currencyFormatter';
import { formatQuantity } from './numberFormatters';
import { COST_LABELS, SALE_LABELS } from './pricingCosting';

/** Underscore metadata worth showing in the details. */
const SHOWN_META = {
  _reason: 'Reason',
  _effective_date: 'Effective date',
  _purchase_orders: 'Purchase orders'
};

/** Field labels that depend on what the record is (a PO line's "unit price" is its cost). */
const ENTITY_FIELD_LABELS = {
  purchase_order: { unit_price: 'Unit Cost', line_amount: 'Line Amount (cost)', unitAmount: 'Unit Cost' },
  customer_order: { unit_price: 'Sale Price', cost_price: 'Cost Price', line_amount: 'Line Amount' },
  invoice: { unit_price: 'Sale Price' }
};

/** Labels for fields only these actions write (the rest go to formatFieldName). */
const FIELD_LABELS = {
  lines: 'Lines',
  file: 'File',
  invoice_to: 'Invoice to',
  invoice: 'Invoice',
  customer: 'Customer',
  order_lines: 'Order lines',
  writes: 'Contract changes',
  contract_id: 'Contract',
  monthly_pricing_id: 'Monthly price',
  cost_price: 'Cost Price',
  line_amount: 'Line Amount',
  xero_status: 'Xero status',
  xero_updated: 'Changed in Xero',
  updated_date_utc: 'Xero version',
  order_lines_costed: 'Order lines costed',
  left_as_they_are: 'Left as they are',
  orders: 'Sales orders',
  purchase_orders: 'Purchase orders',
  // An order's PO archived / restored on its own (PO_ARCHIVED / PO_RESTORED, 2026-11-03)
  purchase_order: 'Purchase order',
  lines_taken_off_the_po: 'Lines taken off the PO',
  lines_put_back_on_the_po: 'Lines put back on the PO',
  order_now: 'The order now',
  error: 'Error',
  edit_reason: 'Reason',
  differs_from_entered: 'Differs from what was entered',
  quantity_differences: 'Quantity differences',
  zero_in_xero_not_taken: 'Zero in Xero (not taken)',
  confirmed: 'Confirmed by the user',
  // A Xero-made PO's lines sent to Xero (XERO_PO_SYNCED, 2026-10-22).
  lines_changed: 'Lines changed in Xero',
  lines_added: 'Lines added in Xero',
  lines_removed: 'Lines removed in Xero',
  header: 'Also changed in Xero'
};

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => v !== null && v !== '' && typeof v !== 'boolean' && !Array.isArray(v) && !Number.isNaN(Number(v));

/** `{ old, new }` and nothing else — the shape every field-by-field renderer expects. */
export function isChangePair(v) {
  if (!isObj(v)) return false;
  const keys = Object.keys(v);
  return keys.length > 0 && keys.every((k) => k === 'old' || k === 'new');
}

/** "£312.00" — a price or cost, to the price rule (2dp, up to 4). */
export function money(v) {
  if (v === null || v === undefined || v === '') return 'none';
  return isNum(v) ? formatPrice(v) : String(v);
}

/** "1 line", "3 lines". */
const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? '' : 's'}`;

/** A key as words: "rep_price" → "rep price". */
const words = (k) => String(k).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();

/** Money-shaped keys inside a line summary. */
const MONEY_KEY = /price|cost|amount|^value$|^total$/i;

/** One value inside a line, in words. */
function piece(key, value) {
  if (value === null || value === undefined || value === '') return 'none';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (isChangePair(value)) return `${piece(key, value.old)} → ${piece(key, value.new)}`;
  if (MONEY_KEY.test(key) && isNum(value)) return money(value);
  if (/quantity|qty/i.test(key) && isNum(value)) return formatQuantity(value);
  if (Array.isArray(value)) return value.map((x) => (isObj(x) ? summariseObject(x) : String(x))).join(', ');
  if (isObj(value)) return summariseObject(value);
  return String(value);
}

/** Any object as "key value · key value", its product / description first. */
function summariseObject(o) {
  const head = o.product || o.description || o.line || o.label || o.po || null;
  const skip = new Set(['product', 'description', 'line', 'label', 'po', 'line_id', 'line_item_id', 'lineItemID']);
  const rest = Object.entries(o)
    .filter(([k, v]) => !skip.has(k) && v !== undefined)
    .map(([k, v]) => `${words(k)} ${piece(k, v)}`);
  if (o.po && o.product) return [`${o.po} · ${o.product}`, ...rest].join(' · ');
  return [head, ...rest].filter(Boolean).join(' · ');
}

/** Where a confirmed cost or sale price came from, in the screen's words. */
function confirmSource(l) {
  if (l.source_ref) return String(l.source_ref);
  const src = String(l.source || '');
  if (!src) return '';
  if (src === 'manual') return 'typed by hand';
  return (l.side === 'cost' ? COST_LABELS[src] : SALE_LABELS[src]) || COST_LABELS[src] || SALE_LABELS[src] || words(src);
}

/** "Sale price · Product (SO-D…)" — whose price a line is. */
function sideAndProduct(l) {
  const side = l.side === 'cost' ? 'Cost' : l.side === 'sale' ? 'Sale price' : null;
  const what = [l.product, l.order ? `(${l.order})` : null].filter(Boolean).join(' ');
  return [side, what].filter(Boolean).join(' · ');
}

/** The fields Xero's version changed on a PO line ("quantity 29.00 → 28.46"). */
function xeroLineDiff(l) {
  const ours = isObj(l.ours) ? l.ours : {};
  const xero = isObj(l.xero) ? l.xero : {};
  const keys = [...new Set([...Object.keys(ours), ...Object.keys(xero)])];
  const diffs = keys
    .filter((k) => JSON.stringify(ours[k] ?? null) !== JSON.stringify(xero[k] ?? null))
    .map((k) => `${words(k)} ${piece(k, ours[k])} → ${piece(k, xero[k])}`);
  const change = l.change === 'added' ? 'only in Xero — added'
    : l.change === 'removed' ? 'not in Xero — removed'
      : l.change || null;
  return [l.line || l.description || 'Line', change, diffs.join('; ')].filter(Boolean).join(' · ');
}

/**
 * One line of a `lines` array as a sentence, by the action that wrote it.
 * Every shape a writer uses is covered; anything else is summarised field by
 * field, never dropped.
 */
export function lineText(action, l) {
  if (l === null || l === undefined) return '';
  if (!isObj(l)) return String(l);
  switch (action) {
    case 'PRICING_CONFIRMED': {
      const src = confirmSource(l);
      return `${sideAndProduct(l)}: ${money(l.value)}${src ? ` — ${src}` : ''}`;
    }
    case 'PRICING_UNCONFIRMED':
      // Undo writes the figure put back (the line's own before the confirm).
      return `${sideAndProduct(l)}: put back to ${money(l.value)} (confirmation undone)`;
    case 'REP_PRICES_IMPORTED':
    case 'REP_PRICE_APPROVED':
    case 'REP_PRICE_HELD':
    case 'REP_PRICE_NOT_TAKEN': {
      const parts = [`${l.product || 'Line'}: rep's price ${money(l.rep_price)}`];
      if (action === 'REP_PRICE_NOT_TAKEN') parts.push(`not taken — kept ${l.kept || 'the confirmed price'}`);
      if (action === 'REP_PRICE_APPROVED') parts.push('approved (sale price confirmed at it)');
      if (action === 'REP_PRICE_HELD') parts.push('held for review');
      if (l.notes) parts.push(`rep's notes: ${l.notes}`);
      return parts.join(' — ');
    }
    case 'LINES_INVOICED':
      return `${l.product || 'Line'}: ${isNum(l.quantity) ? formatQuantity(l.quantity) : '?'} at ${money(l.price)}`;
    case 'XERO_VERSION_ACCEPTED':
    case 'XERO_VERSION_KEPT':
      return xeroLineDiff(l);
    case 'PO_COSTS_FROM_BILL': {
      const parts = [[l.po, l.product].filter(Boolean).join(' · ') || 'Line'];
      if (isChangePair(l.po_cost)) parts.push(`PO cost ${money(l.po_cost.old)} → ${money(l.po_cost.new)}`);
      if (isChangePair(l.order_line_cost)) parts.push(`order line cost ${money(l.order_line_cost.old)} → ${money(l.order_line_cost.new)}`);
      return parts.join(': ');
    }
    case 'XERO_COST_RESYNCED':
      return `${l.product || 'Line'}: cost ${money(l.old)} → ${money(l.new)} (taken from Xero)`;
    default:
      if ('old' in l && 'new' in l && Object.keys(l).length <= 4) {
        return `${l.product || l.label || 'Line'}: ${money(l.old)} → ${money(l.new)}`;
      }
      return summariseObject(l);
  }
}

/** "create #2748" → "Created contract #2748". */
function writeText(w) {
  const m = /^(\w+)\s+#?(\d+)$/.exec(String(w));
  if (!m) return String(w);
  const verb = { create: 'Created', price: 'Price changed on', hold: 'Put on hold', archive: 'Archived' }[m[1]] || m[1];
  return `${verb} contract #${m[2]}`;
}

/** Ids shown with their resolved labels when the server sent them. */
function idList(ids, labels) {
  return (Array.isArray(ids) ? ids : [ids]).map((id) => {
    const label = labels && labels[String(id)];
    return label ? `${label} (line ${id})` : `Line ${id}`;
  });
}

/** The rows a price request to a rep becomes. */
function priceRequestRows(v, resolved) {
  const rows = [];
  if (v.to) rows.push({ kind: 'value', field: 'to', label: 'Sent to', value: v.to });
  if (Array.isArray(v.line_ids)) {
    rows.push({ kind: 'list', field: 'line_ids', label: `Lines asked about (${v.line_ids.length})`, items: idList(v.line_ids, resolved?.line_ids) });
  }
  if (v.spreadsheet) rows.push({ kind: 'value', field: 'spreadsheet', label: 'Spreadsheet', value: v.spreadsheet });
  if (v.second_check) rows.push({ kind: 'value', field: 'second_check', label: 'Second price check', value: true });
  return rows;
}

/** The rows a cost sent to Xero becomes. */
function xeroCostRows(v) {
  if (!isObj(v)) return [];
  if (v.error) {
    return [
      { kind: 'value', field: 'xero_cost', label: 'Cost sent to Xero', value: 'No', text: `No — ${v.error}` },
      ...(v.xero_status ? [{ kind: 'value', field: 'xero_status', label: 'Xero status', value: v.xero_status }] : []),
      ...(v.code ? [{ kind: 'value', field: 'code', label: 'Why', value: v.code === 'XERO_PO_BILLED_IN_XERO' ? 'Billed in Xero — change it there, then Update cost from Xero' : v.code }] : []),
      { kind: 'value', field: 'retryable', label: 'Will be tried again', value: !!v.retryable }
    ];
  }
  const sent = Number(v.sent) || 0;
  return [
    { kind: 'value', field: 'xero_cost', label: 'Cost sent to Xero',
      value: sent, text: v.unchanged ? 'Already the same in Xero — nothing to send' : `${plural(sent, 'line')} sent` },
    ...(v.xero_status ? [{ kind: 'value', field: 'xero_status', label: 'Xero status', value: v.xero_status }] : []),
    { kind: 'value', field: 'unverified', label: 'Checked in Xero', value: !v.unverified,
      text: v.unverified ? 'No — Xero could not be read back' : 'Yes — read back from Xero' }
  ];
}

/** The rows a Xero-made PO's lines sent to Xero become (XERO_PO_SYNCED / _FAILED). */
function xeroSyncRows(v) {
  if (!isObj(v)) return [];
  if (v.error) {
    const why = v.code === 'XERO_PO_BILLED_IN_XERO' ? 'Billed in Xero — change it there, then make the same change here'
      : v.code === 'XERO_PO_NEWER' ? 'Changed in Xero since it was read' : (v.code || null);
    return [
      { kind: 'value', field: 'xero_sync', label: 'Sent to Xero', value: 'No', text: `No — ${v.error}${v.restored === true ? " (Xero's copy put back)" : v.restored === false ? " (Xero's copy could not be put back — check it)" : ''}` },
      ...(why ? [{ kind: 'value', field: 'code', label: 'Why', value: why }] : []),
      { kind: 'value', field: 'retryable', label: 'Can be tried again', value: !!v.retryable }
    ];
  }
  return [
    { kind: 'value', field: 'xero_sync', label: 'Sent to Xero', value: Number(v.sent) || 0, text: `${plural(Number(v.sent) || 0, 'line')} sent` },
    ...(v.xero_status ? [{ kind: 'value', field: 'xero_status', label: 'Xero status', value: v.xero_status }] : []),
    { kind: 'value', field: 'verified', label: 'Checked in Xero', value: v.verified !== false,
      text: v.verified === false ? 'No — Xero could not be read back' : 'Yes — read back from Xero' }
  ];
}

/** The label for a field on this record. */
export function fieldLabel(field, entityType, formatFieldName) {
  const byEntity = ENTITY_FIELD_LABELS[entityType];
  if (byEntity && byEntity[field]) return byEntity[field];
  if (SHOWN_META[field]) return SHOWN_META[field];
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  if (formatFieldName) return formatFieldName(field);
  const w = words(field);
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/**
 * Every visible field of an entry's changes, as rows.
 *
 * @param {object} changes   the audit row's changes
 * @param {object} [o]
 * @param {string} [o.action]
 * @param {string} [o.entityType]
 * @param {object} [o.resolved]  the server's id → label lookups, per field
 * @param {Function} [o.formatFieldName]  the screens' own field names
 * @returns {Array<object>}
 */
export function describeChanges(changes, { action = '', entityType = '', resolved = null, formatFieldName = null } = {}) {
  if (!isObj(changes)) return [];
  const rows = [];
  const label = (f) => fieldLabel(f, entityType, formatFieldName);
  for (const [field, raw] of Object.entries(changes)) {
    if (raw === undefined) continue;
    if (field.startsWith('_') && !SHOWN_META[field]) continue;

    // Fields whose shape needs its own words.
    if (field === 'price_requested' && isChangePair(raw) && isObj(raw.new)) {
      rows.push(...priceRequestRows(raw.new, resolved));
      continue;
    }
    if (field === 'xero_cost' && isChangePair(raw)) {
      rows.push(...xeroCostRows(raw.new));
      continue;
    }
    if (field === 'xero_sync' && isChangePair(raw)) {
      rows.push(...xeroSyncRows(raw.new));
      continue;
    }
    if (field === 'header' && Array.isArray(raw)) {
      rows.push({ kind: 'list', field, label: label(field), items: raw.map(h => `${words(h?.field || 'field')} ${h?.old ?? 'none'} → ${h?.new ?? 'none'}`) });
      continue;
    }
    if (field === 'writes' && Array.isArray(raw)) {
      rows.push({ kind: 'list', field, label: label(field), items: raw.map(writeText) });
      continue;
    }
    if (field === 'order_lines' && Array.isArray(raw)) {
      rows.push({ kind: 'list', field, label: `${label(field)} (${raw.length})`, items: idList(raw, resolved?.order_lines) });
      continue;
    }
    if (field === 'lines' && isNum(raw)) {
      rows.push({ kind: 'value', field, label: label(field), value: Number(raw), text: plural(Number(raw), 'line') });
      continue;
    }

    if (isChangePair(raw)) {
      rows.push({
        kind: 'pair', field, label: label(field),
        old: raw.old, new: raw.new, hasOld: raw.old !== undefined, hasNew: raw.new !== undefined
      });
      continue;
    }
    if (Array.isArray(raw)) {
      if (!raw.length) { rows.push({ kind: 'value', field, label: label(field), value: null, text: 'None' }); continue; }
      const items = raw.map((l) => lineText(action, l)).filter(Boolean);
      rows.push({ kind: 'list', field, label: raw.length > 1 ? `${label(field)} (${raw.length})` : label(field), items });
      continue;
    }
    if (isObj(raw)) {
      rows.push({ kind: 'value', field, label: label(field), value: raw, text: summariseObject(raw) });
      continue;
    }
    rows.push({ kind: 'value', field, label: label(field), value: raw });
  }
  return rows;
}

/**
 * What an entry is about beyond its record — the product and line the
 * route recorded (`_product`, `_item_code`, `_po`, a resolved order line).
 * Shown beside the identifier on the Activity screen.
 */
export function contextOf(entry) {
  const c = entry?.changes;
  if (!isObj(c)) return '';
  const parts = [];
  const text = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '');
  const product = text(c._product);
  const code = text(c._item_code);
  if (product) parts.push(code && !product.includes(code) ? `${code} ${product}` : product);
  else if (code) parts.push(code);
  const po = text(c._po);
  if (po && po !== entry.entity_identifier) parts.push(po);
  const ol = c._order_line_id ?? null;
  if (!product && ol != null) {
    const label = entry?.resolved?._order_line_id?.[String(ol)];
    parts.push(label || `line ${ol}`);
  }
  return parts.join(' · ');
}

/** The tracked email an entry's send can be opened from (a price request to a rep). */
export function emailIdOf(entry) {
  const pr = entry?.changes?.price_requested;
  const id = isChangePair(pr) && isObj(pr.new) ? pr.new.tracking_id : null;
  return id !== null && id !== undefined && id !== '' && !Number.isNaN(Number(id)) ? Number(id) : null;
}

/*
 * Which records open in their own window from the Activity screen
 * (EntityModalProvider), and the id that window takes. A PO's window takes its
 * Xero id (or LOCAL-/TEMP-), which /api/audit/search sends as `open_id`.
 */
const OPENABLE = {
  customer_order: (e) => e.entity_id,
  invoice: (e) => e.entity_id,
  bill: (e) => e.entity_id,
  contact: (e) => e.entity_id,
  inventory_item: (e) => e.entity_id,
  purchase_order: (e) => e.open_id || null
};

/** The id an entry's record opens with, or null when it has no window here. */
export function openIdOf(entry) {
  const f = OPENABLE[entry?.entity_type];
  const id = f ? f(entry) : null;
  return id === null || id === undefined || id === '' ? null : id;
}
